import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { sessions, agents } from '../api';
import {
  Download, MessageSquare, Clock, RefreshCw,
  Search, ChevronDown, ChevronLeft, ChevronRight, Calendar, Bot,
  FileSpreadsheet, X, Check, Eye, Columns3, Database,
  FileText, User, DollarSign, Info, ClipboardList,
} from 'lucide-react';
import Toast from './Toast';
import LoadingSpinner from './LoadingSpinner';
import VisitorBadge, { visitorExportText } from './VisitorBadge';
import { ChatMessageImages, chatImagesFromIds } from './chatImageAttachments';
import AnimatedDropdown from './AnimatedDropdown';

// ─── Themed Calendar Picker ───────────────────────────────────────────────────

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_LABELS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const ThemedDatePicker = ({ value, onChange, label, icon: Icon, maxDate, minDate }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const selected = value ? new Date(value + 'T00:00:00') : null;
  const [viewYear, setViewYear] = useState(selected ? selected.getFullYear() : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(selected ? selected.getMonth() : new Date().getMonth());

  useEffect(() => {
    if (open && selected) {
      setViewYear(selected.getFullYear());
      setViewMonth(selected.getMonth());
    }
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };

  const isDisabled = (d) => {
    const ds = toDateStr(d);
    if (maxDate && ds > maxDate) return true;
    if (minDate && ds < minDate) return true;
    return false;
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const prevDays = new Date(viewYear, viewMonth, 0).getDate();
  const cells = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ day: prevDays - i, outside: true, date: new Date(viewYear, viewMonth - 1, prevDays - i) });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, outside: false, date: new Date(viewYear, viewMonth, d) });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, outside: true, date: new Date(viewYear, viewMonth + 1, d) });
  }

  const today = new Date();
  const todayStr = toDateStr(today);

  const displayValue = selected
    ? selected.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  const panelPosition = () => {
    if (!btnRef.current) return {};
    const r = btnRef.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    if (below < 360) {
      return { position: 'fixed', bottom: window.innerHeight - r.top + 8, left: r.left, width: Math.max(r.width, 310), zIndex: 99999 };
    }
    return { position: 'fixed', top: r.bottom + 8, left: r.left, width: Math.max(r.width, 310), zIndex: 99999 };
  };

  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        {Icon && <Icon className="w-4 h-4 inline mr-1.5" />}
        {label}
      </label>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full bg-background border ${
          open ? 'border-primary shadow-lg shadow-primary/20' : 'border-primary/50 hover:border-primary hover:shadow-lg hover:shadow-primary/10'
        } rounded-lg px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 flex items-center justify-between cursor-pointer`}
      >
        <span className={`flex items-center gap-2 ${displayValue ? 'text-foreground' : 'text-muted-foreground'}`}>
          <Calendar className="w-4 h-4 flex-shrink-0 text-primary" />
          {displayValue || 'Select date'}
        </span>
        <ChevronDown className={`w-4 h-4 text-primary transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={panelPosition()}
          className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl shadow-elegant overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
            <button type="button" onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-foreground select-none">
              {MONTH_NAMES[viewMonth]} {viewYear}
            </span>
            <button type="button" onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 px-3 pt-3 pb-1">
            {DAY_LABELS.map((d) => (
              <div key={d} className="text-center text-[11px] font-semibold text-muted-foreground uppercase tracking-wider py-1">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 px-3 pb-3 gap-y-0.5">
            {cells.map((c, i) => {
              const ds = toDateStr(c.date);
              const isSel = value === ds;
              const isToday = ds === todayStr;
              const disabled = isDisabled(c.date);
              return (
                <button
                  key={i}
                  type="button"
                  disabled={disabled || c.outside}
                  onClick={() => { onChange(ds); setOpen(false); }}
                  className={`
                    relative w-full aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-150
                    ${c.outside ? 'text-muted-foreground/30 cursor-default' : ''}
                    ${disabled && !c.outside ? 'text-muted-foreground/40 cursor-not-allowed' : ''}
                    ${!c.outside && !disabled && !isSel ? 'hover:bg-primary/10 hover:text-primary cursor-pointer text-foreground' : ''}
                    ${isSel ? 'bg-primary text-primary-foreground shadow-md shadow-primary/30 hover:bg-primary/90' : ''}
                    ${isToday && !isSel ? 'ring-1 ring-primary/50' : ''}
                  `}
                >
                  {c.day}
                  {isToday && !isSel && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2 px-3 pb-3">
            <button
              type="button"
              onClick={() => { onChange(todayStr); setOpen(false); }}
              className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            >
              Today
            </button>
            {value && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); }}
                className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-border/60 text-muted-foreground hover:text-destructive hover:border-destructive/30 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ─── Column Visibility Dropdown ───────────────────────────────────────────────

const ColumnVisibilityDropdown = ({ columns, visible, onToggle, onShowAll, onHideAll }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const visibleCount = columns.filter((c) => visible[c.key]).length;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="px-4 py-2 bg-card border border-border hover:border-primary/50 text-foreground rounded-lg transition-all duration-200 flex items-center gap-2 text-sm font-medium hover:shadow-md"
      >
        <Columns3 className="w-4 h-4 text-primary" />
        Columns
        <span className="text-xs text-muted-foreground">({visibleCount})</span>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-card border border-border rounded-xl shadow-elegant z-50 overflow-hidden">
          <div className="p-3 border-b border-border bg-muted/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <Eye className="w-4 h-4 text-primary" />
                Visible Columns
              </span>
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex gap-2">
              <button onClick={onShowAll} className="text-xs text-primary hover:underline">Show All</button>
              <span className="text-xs text-muted-foreground">|</span>
              <button onClick={onHideAll} className="text-xs text-primary hover:underline">Hide All</button>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto p-2 space-y-0.5">
            {columns.map((col) => (
              <button
                key={col.key}
                type="button"
                onClick={() => onToggle(col.key)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer transition-colors text-left"
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                  visible[col.key] ? 'bg-primary border-primary' : 'border-border bg-background'
                }`}>
                  {visible[col.key] && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-sm text-foreground">{col.label}</span>
                {col.isExtraction && <span className="text-[10px] text-primary/60 ml-auto bg-primary/10 px-1.5 py-0.5 rounded">extracted</span>}
                {col.isFormField && <span className="text-[10px] text-warning/70 ml-auto bg-warning/10 px-1.5 py-0.5 rounded">form</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Transcript Modal ─────────────────────────────────────────────────────────

// Message times are stored per entry as ISO 8601 UTC (see _upsert_analysis in
// routes/agents.py). Sessions recorded before that was added carry no timestamp,
// so every use of one has to tolerate its absence rather than print "Invalid Date".
const messageTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const TranscriptModal = ({ session, onClose }) => {
  const hasStructured = Array.isArray(session.transcript_object) && session.transcript_object.length > 0;

  const messages = hasStructured
    ? session.transcript_object.filter((msg) => msg.role === 'user' || msg.role === 'assistant')
    : [];
  const anyTimestamps = messages.some((msg) => messageTime(msg.timestamp));
  // A chat can run across midnight, and a resumed one can span days. Showing the
  // date on every bubble would be noise, so it appears only when the day changes.
  const spansDays =
    new Set(
      messages
        .map((msg) => messageTime(msg.timestamp))
        .filter(Boolean)
        .map((d) => d.toDateString())
    ).size > 1;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-muted/30 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <FileText className="w-5 h-5 text-primary" />
            <div>
              <h3 className="text-sm font-semibold text-foreground">Chat Transcript</h3>
              <p className="text-xs text-muted-foreground">
                {session.agent_name} · {session.started_at ? new Date(session.started_at).toLocaleString() : ''}
              </p>
              {hasStructured && !anyTimestamps && (
                // Chats recorded before per-message times were stored. Say so,
                // rather than leaving an admin wondering where the times went.
                <p className="text-[11px] text-muted-foreground/70">Message times were not recorded for this chat.</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {hasStructured ? (
            messages.map((msg, idx) => {
                const isUser = msg.role === 'user';
                const sentAt = messageTime(msg.timestamp);
                return (
                  <div key={idx} className={`flex gap-2.5 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                    <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shadow-sm ${
                      isUser ? 'bg-primary text-primary-foreground' : 'bg-muted border border-border text-muted-foreground'
                    }`}>
                      {isUser ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                    </div>
                    <div className={`max-w-[80%] flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
                      <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                        isUser
                          ? 'bg-primary text-primary-foreground rounded-tr-sm'
                          : 'bg-muted/60 border border-border/60 text-foreground rounded-tl-sm'
                      }`}>
                        {msg.images?.length > 0 && (
                          <ChatMessageImages images={chatImagesFromIds(session.agent_id, msg.images)} />
                        )}
                        {msg.content}
                      </div>
                      {sentAt && (
                        <span
                          className="text-[10px] text-muted-foreground mt-1 px-1 tabular-nums"
                          title={sentAt.toLocaleString()}
                        >
                          {spansDays
                            ? sentAt.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                            : sentAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
          ) : session.transcript ? (
            <div className="bg-background/50 rounded-xl border border-border/60 p-4">
              <pre className="text-xs text-foreground whitespace-pre-wrap font-mono leading-relaxed">
                {session.transcript}
              </pre>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="w-10 h-10 text-muted-foreground opacity-40 mb-3" />
              <p className="text-sm text-muted-foreground">No transcript available for this session.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border bg-muted/20 flex-shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Cost Breakdown Tooltip ───────────────────────────────────────────────────

const CostBreakdownTooltip = ({ breakdown }) => {
  const [show, setShow] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); };
    if (show) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [show]);

  if (!breakdown?.costs) return null;
  const { chat_cost, llm_cost, tts_cost, stt_cost, infrastructure_cost } = breakdown.costs;
  const providers = breakdown.providers || {};

  // A chat session is billed one of two ways, and the breakdown has to say
  // which. Per-minute sessions carry the whole charge on chat_cost and leave
  // every other line at zero, so rendering only the LLM/TTS/STT lines showed
  // four zeros above a non-zero total. Token-billed sessions carry it on
  // llm_cost. TTS, STT and telephony never apply to a text chat, so those lines
  // only appear if a rate somehow landed on them.
  const perMinute = breakdown.billing_model === 'per_minute';
  const rows = [];
  if (perMinute) {
    const minutes = breakdown.duration_minutes;
    const rate = breakdown.price_per_minute_usd;
    rows.push({
      key: 'chat',
      label: minutes != null && rate != null
        ? `Chat (${minutes.toFixed(2)} min x $${rate}/min)`
        : 'Chat (per minute)',
      value: chat_cost,
    });
  } else if (llm_cost != null) {
    rows.push({ key: 'llm', label: `LLM (${providers.llm || 'GPT'})`, value: llm_cost });
  }
  if (tts_cost) rows.push({ key: 'tts', label: `TTS (${providers.tts || 'ElevenLabs'})`, value: tts_cost });
  if (stt_cost) rows.push({ key: 'stt', label: `STT (${providers.stt || 'Deepgram'})`, value: stt_cost });
  if (infrastructure_cost) rows.push({ key: 'infra', label: 'Infrastructure', value: infrastructure_cost });

  return (
    <div className="relative inline-flex" ref={ref}>
      <button
        onClick={() => setShow(!show)}
        className="ml-1 text-muted-foreground hover:text-primary transition-colors"
        title="View cost breakdown"
      >
        <Info className="w-3 h-3" />
      </button>
      {show && (
        <div className="absolute right-0 bottom-full mb-2 w-64 bg-card border border-border rounded-xl shadow-elegant z-50 p-3 space-y-1.5 text-xs">
          <p className="font-semibold text-foreground mb-2 flex items-center gap-1">
            <DollarSign className="w-3 h-3 text-primary" /> Cost Breakdown
          </p>
          {rows.map((row) => (
            <div key={row.key} className="flex justify-between gap-2 text-muted-foreground">
              <span>{row.label}</span>
              <span className="text-foreground font-medium whitespace-nowrap">${(row.value ?? 0).toFixed(5)}</span>
            </div>
          ))}
          <div className="border-t border-border pt-1.5 mt-1 flex justify-between font-semibold text-foreground">
            <span>Total</span>
            <span className="text-success">${breakdown.costs.total_cost?.toFixed(4)}</span>
          </div>
          <p className="text-[10px] text-muted-foreground pt-0.5 border-t border-border/50 mt-1">
            {perMinute
              ? 'Billed at the account chat rate x session duration'
              : 'Calculated from actual token usage'}
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ChatHistoryExport = () => {
  const [agentsList, setAgentsList] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sessionsList, setSessionsList] = useState([]);
  const [extractionFields, setExtractionFields] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sessionsPerPage] = useState(20);
  const [toast, setToast] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [transcriptSession, setTranscriptSession] = useState(null);

  const [exportOpen, setExportOpen] = useState(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState({});
  const exportRef = useRef(null);

  const [visibleColumns, setVisibleColumns] = useState({});
  const initialLoadDone = useRef(false);

  const baseColumns = [
    { key: 'visitor', label: 'Visitor' },
    { key: 'agent_name', label: 'Agent' },
    { key: 'duration', label: 'Chat Duration' },
    { key: 'status', label: 'Status' },
    { key: 'sentiment', label: 'Sentiment' },
    { key: 'summary', label: 'Summary' },
    { key: 'transcript', label: 'Transcript' },
    { key: 'started_at', label: 'Start Time' },
    { key: 'ended_at', label: 'End Time' },
    { key: 'cost_usd', label: 'Chat Cost (USD)' },
  ];

  const allTableColumns = [
    ...baseColumns,
    ...extractionFields.map((f) => ({ key: `ext_${f.name}`, label: f.name, isExtraction: true })),
    ...formFields.map((f) => ({ key: `form_${f.name}`, label: f.label || f.name, isFormField: true })),
  ];

  const allExportColumns = [
    ...baseColumns,
    ...extractionFields.map((f) => ({ key: `ext_${f.name}`, label: f.name })),
    ...formFields.map((f) => ({ key: `form_${f.name}`, label: f.label || f.name })),
  ];

  useEffect(() => {
    loadAgents();
    const defaults = {};
    baseColumns.forEach((c) => { defaults[c.key] = true; });
    setVisibleColumns(defaults);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false);
    };
    if (exportOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportOpen]);

  const getDefaultDates = () => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };
    return { start: fmt(start), end: fmt(end) };
  };

  const handleAgentChange = useCallback((agentId) => {
    setSelectedAgentId(agentId);
    if (!agentId) return;
    const { start, end } = getDefaultDates();
    setStartDate(start);
    setEndDate(end);
  }, []);

  useEffect(() => {
    if (selectedAgentId && startDate && endDate && !initialLoadDone.current) {
      initialLoadDone.current = true;
      doLoadChatHistory(selectedAgentId, startDate, endDate, 1);
    }
  }, [selectedAgentId, startDate, endDate]);

  const loadAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await agents.list(false);
      const list = response.data || [];
      setAgentsList(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to load agents:', error);
      setToast({ message: 'Failed to load agents', type: 'error' });
    } finally {
      setLoadingAgents(false);
    }
  };

  const doLoadChatHistory = async (agentId, sd, ed, page) => {
    if (!agentId || !sd || !ed) return;
    try {
      setLoading(true);
      const response = await sessions.chatHistory(agentId, sd, ed, page, sessionsPerPage);
      const data = response.data;
      setSessionsList(data.sessions || []);
      setExtractionFields(data.extraction_fields || []);

      // Merge the agent's configured form fields with any variable keys found on
      // the loaded sessions (covers sessions submitted before the form config changed).
      const backendFormFields = data.form_fields || [];
      const knownNames = new Set(backendFormFields.map((f) => f.name));
      const extraFields = [];
      (data.sessions || []).forEach((s) => {
        Object.keys(s.form_variables || {}).forEach((k) => {
          if (!knownNames.has(k)) { knownNames.add(k); extraFields.push({ name: k, label: k }); }
        });
      });
      const mergedFormFields = [...backendFormFields, ...extraFields];
      setFormFields(mergedFormFields);

      setTotalSessions(data.total || 0);
      setTotalPages(data.total_pages || 0);
      setCurrentPage(data.page || 1);
      setHasFetched(true);

      const expDefaults = {};
      baseColumns.forEach((c) => { expDefaults[c.key] = true; });
      (data.extraction_fields || []).forEach((f) => { expDefaults[`ext_${f.name}`] = true; });
      mergedFormFields.forEach((f) => { expDefaults[`form_${f.name}`] = true; });
      setSelectedExportColumns(expDefaults);

      setVisibleColumns((prev) => {
        const next = { ...prev };
        (data.extraction_fields || []).forEach((f) => {
          const k = `ext_${f.name}`;
          if (next[k] === undefined) next[k] = true;
        });
        mergedFormFields.forEach((f) => {
          const k = `form_${f.name}`;
          if (next[k] === undefined) next[k] = true;
        });
        return next;
      });
    } catch (error) {
      console.error('Failed to load chat history:', error);
      setToast({ message: 'Failed to load chat history', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadChatHistory = (page = 1) => {
    doLoadChatHistory(selectedAgentId, startDate, endDate, page);
  };

  const handleSearch = () => {
    if (!selectedAgentId || !startDate || !endDate) {
      setToast({ message: 'Please select an agent and date range', type: 'error' });
      return;
    }
    initialLoadDone.current = true;
    setCurrentPage(1);
    loadChatHistory(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadChatHistory(page);
  };

  const toggleVisibleColumn = (key) => setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  const showAllColumns = () => {
    const v = {};
    allTableColumns.forEach((c) => { v[c.key] = true; });
    setVisibleColumns(v);
  };
  const hideAllColumns = () => setVisibleColumns({});

  const toggleExportColumn = (key) => setSelectedExportColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  const selectAllExportCols = () => {
    const all = {};
    allExportColumns.forEach((c) => { all[c.key] = true; });
    setSelectedExportColumns(all);
  };
  const deselectAllExportCols = () => setSelectedExportColumns({});

  const exportToCSV = async () => {
    if (!selectedAgentId || !startDate || !endDate) return;
    const chosen = allExportColumns.filter((c) => selectedExportColumns[c.key]);
    if (chosen.length === 0) {
      setToast({ message: 'Please select at least one column to export', type: 'error' });
      return;
    }
    try {
      setToast({ message: 'Preparing export...', type: 'info' });
      let allSessionsData = [];
      let page = 1;
      const batchSize = 100;
      let hasMore = true;
      while (hasMore) {
        const response = await sessions.chatHistory(selectedAgentId, startDate, endDate, page, batchSize);
        const data = response.data;
        allSessionsData = allSessionsData.concat(data.sessions || []);
        hasMore = page < (data.total_pages || 0);
        page++;
      }
      const escapeCSV = (val) => {
        if (val == null) return '';
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) return `"${str.replace(/"/g, '""')}"`;
        return str;
      };
      const header = chosen.map((c) => escapeCSV(c.label)).join(',');
      const rows = allSessionsData.map((s) => chosen.map((c) => escapeCSV(getExportValue(s, c.key))).join(','));
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const agentName = agentsList.find((a) => a.id === selectedAgentId)?.name || 'agent';
      link.href = url;
      link.download = `chat-history_${agentName}_${startDate}_${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setToast({ message: `Exported ${allSessionsData.length} records successfully`, type: 'success' });
      setExportOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      setToast({ message: 'Export failed. Please try again.', type: 'error' });
    }
  };

  const getExportValue = (session, key) => {
    if (key.startsWith('ext_')) {
      const fieldName = key.slice(4);
      return session.extracted_data?.[fieldName] ?? '-';
    }
    if (key.startsWith('form_')) {
      const fieldName = key.slice(5);
      const value = session.form_variables?.[fieldName];
      return value != null && value !== '' ? value : '-';
    }
    switch (key) {
      case 'visitor': return visitorExportText(session.visitor);
      case 'agent_name': return session.agent_name || 'Unknown';
      case 'duration': return session.duration != null ? formatDuration(session.duration) : 'N/A';
      case 'status': return session.status || '-';
      case 'sentiment': return session.sentiment || 'Neutral';
      case 'summary': return session.summary || '-';
      case 'transcript': return session.transcript || '-';
      case 'started_at': return session.started_at ? new Date(session.started_at).toLocaleString('en-US', { timeZone: 'America/New_York' }) + ' ET' : '-';
      case 'ended_at': return session.ended_at ? new Date(session.ended_at).toLocaleString('en-US', { timeZone: 'America/New_York' }) + ' ET' : '-';
      case 'cost_usd': return session.cost_usd != null ? `$${session.cost_usd.toFixed(4)}` : '-';
      default: return '-';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-primary/20 text-primary border-primary/30';
      case 'ended': case 'completed': return 'bg-success/20 text-success border-success/30';
      case 'failed': case 'error': case 'terminated': return 'bg-destructive/20 text-destructive border-destructive/30';
      case 'abandoned': return 'bg-warning/20 text-warning border-warning/30';
      default: return 'bg-muted/20 text-muted-foreground border-border';
    }
  };

  const getSentimentColor = (sentiment) => {
    if (!sentiment) return 'text-muted-foreground';
    switch (sentiment.toLowerCase()) {
      case 'positive': return 'text-success';
      case 'negative': return 'text-destructive';
      default: return 'text-foreground';
    }
  };

  const agentOptions = agentsList.map((a) => ({ value: a.id, label: a.name }));
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const visibleBaseColumns = baseColumns.filter((c) => visibleColumns[c.key]);
  const visibleExtractionFields = extractionFields.filter((f) => visibleColumns[`ext_${f.name}`]);
  const visibleFormFields = formFields.filter((f) => visibleColumns[`form_${f.name}`]);

  const renderCell = (session, colKey) => {
    switch (colKey) {
      case 'visitor':
        return <VisitorBadge visitor={session.visitor} />;
      case 'agent_name':
        return (
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <span className="text-sm text-foreground truncate font-medium max-w-[120px]">{session.agent_name || 'Unknown'}</span>
          </div>
        );
      case 'duration':
        return (
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <Clock className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
            <span className="text-sm text-foreground font-medium">{formatDuration(session.duration)}</span>
          </div>
        );
      case 'status':
        return (
          <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border whitespace-nowrap shadow-sm ${getStatusColor(session.status)}`}>
            {(session.status || '').toLowerCase()}
          </span>
        );
      case 'sentiment':
        return (
          <span className={`text-sm font-semibold capitalize px-2 py-1 rounded whitespace-nowrap ${getSentimentColor(session.sentiment)}`}>
            {session.sentiment || 'Neutral'}
          </span>
        );
      case 'summary':
        return (
          <div className="max-w-[300px] min-w-[180px]">
            {session.summary ? (
              <p className="text-sm text-foreground line-clamp-3 leading-relaxed" title={session.summary}>
                {session.summary}
              </p>
            ) : (
              <span className="text-sm text-muted-foreground">-</span>
            )}
          </div>
        );
      case 'transcript': {
        const hasTranscript = session.transcript || (Array.isArray(session.transcript_object) && session.transcript_object.length > 0);
        return (
          <button
            onClick={() => hasTranscript && setTranscriptSession(session)}
            disabled={!hasTranscript}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 ${
              hasTranscript
                ? 'bg-primary/10 border-primary/30 text-primary hover:bg-primary/20 hover:border-primary/50 shadow-sm'
                : 'bg-muted/20 border-border text-muted-foreground cursor-not-allowed opacity-50'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            {hasTranscript ? 'View' : 'None'}
          </button>
        );
      }
      case 'started_at':
        return (
          <div className="flex flex-col gap-0.5 min-w-[155px]">
            <span className="text-sm text-foreground font-medium whitespace-nowrap">
              {formatDate(session.started_at)} <span className="text-[10px] text-muted-foreground font-normal">ET</span>
            </span>
            {session.ended_at && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                Ended: {formatDate(session.ended_at)}
              </span>
            )}
          </div>
        );
      case 'ended_at':
        return <span className="text-sm text-foreground font-medium whitespace-nowrap">{session.ended_at ? formatDate(session.ended_at) : '-'}</span>;
      case 'cost_usd':
        return session.cost_usd != null
          ? (
            <div className="flex items-center justify-end gap-0.5">
              <span className="text-sm text-success font-semibold">${session.cost_usd.toFixed(4)}</span>
              {session.cost_breakdown && <CostBreakdownTooltip breakdown={session.cost_breakdown} />}
            </div>
          )
          : <span className="text-sm text-muted-foreground">-</span>;
      default:
        return <span className="text-sm text-muted-foreground">-</span>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-foreground mb-1 flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-primary" />
          Chat History & Export
        </h2>
        <p className="text-sm text-muted-foreground">View text chat sessions from embedded widgets — duration, LLM cost, transcript, extracted data, and CSV export.</p>
      </div>

      {/* Filters */}
      <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-5 shadow-lg shadow-primary/5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          {/* Agent Selector */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              <Bot className="w-4 h-4 inline mr-1.5" />
              Agent
            </label>
            {loadingAgents ? (
              <div className="flex items-center justify-center h-[46px] bg-background border border-border/50 rounded-lg">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              </div>
            ) : (
              <AnimatedDropdown
                value={selectedAgentId}
                onChange={handleAgentChange}
                options={agentOptions}
                placeholder="Select an agent"
                icon={Bot}
              />
            )}
          </div>

          {/* Start Date */}
          <ThemedDatePicker
            value={startDate}
            onChange={setStartDate}
            label="Start Date"
            icon={Calendar}
            maxDate={endDate || todayStr}
          />

          {/* End Date */}
          <ThemedDatePicker
            value={endDate}
            onChange={setEndDate}
            label="End Date"
            icon={Calendar}
            minDate={startDate}
            maxDate={todayStr}
          />

          {/* Search Button */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2 invisible">Search</label>
            <button
              onClick={handleSearch}
              disabled={!selectedAgentId || !startDate || !endDate || loading}
              className="w-full px-4 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Search className="w-4 h-4" />
              )}
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {hasFetched && (
        <>
          {/* Action bar */}
          <div className="flex flex-wrap justify-between items-center gap-2">
            <div className="text-sm text-muted-foreground">
              {totalSessions} chat{totalSessions !== 1 ? 's' : ''} found
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <ColumnVisibilityDropdown
                columns={allTableColumns}
                visible={visibleColumns}
                onToggle={toggleVisibleColumn}
                onShowAll={showAllColumns}
                onHideAll={hideAllColumns}
              />

              <button
                onClick={() => loadChatHistory(currentPage)}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>

              {/* Export Dropdown */}
              <div className="relative" ref={exportRef}>
                <button
                  onClick={() => setExportOpen(!exportOpen)}
                  disabled={sessionsList.length === 0}
                  className="px-4 py-2 bg-success hover:bg-success/90 text-white rounded-lg transition-colors flex items-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${exportOpen ? 'rotate-180' : ''}`} />
                </button>

                {exportOpen && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-xl shadow-elegant z-50 overflow-hidden">
                    <div className="p-3 border-b border-border bg-muted/30">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                          <FileSpreadsheet className="w-4 h-4 text-primary" />
                          Select Columns to Export
                        </span>
                        <button onClick={() => setExportOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={selectAllExportCols} className="text-xs text-primary hover:underline">Select All</button>
                        <span className="text-xs text-muted-foreground">|</span>
                        <button onClick={deselectAllExportCols} className="text-xs text-primary hover:underline">Deselect All</button>
                      </div>
                    </div>

                    <div className="max-h-64 overflow-y-auto p-2 space-y-0.5">
                      <div className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Standard Columns</div>
                      {baseColumns.map((col) => (
                        <button
                          key={col.key}
                          type="button"
                          onClick={() => toggleExportColumn(col.key)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer transition-colors text-left"
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                            selectedExportColumns[col.key] ? 'bg-primary border-primary' : 'border-border bg-background'
                          }`}>{selectedExportColumns[col.key] && <Check className="w-3 h-3 text-white" />}</div>
                          <span className="text-sm text-foreground">{col.label}</span>
                        </button>
                      ))}
                      {extractionFields.length > 0 && (
                        <>
                          <div className="px-2 py-1 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Extracted Data</div>
                          {extractionFields.map((f) => (
                            <button
                              key={`ext_${f.name}`}
                              type="button"
                              onClick={() => toggleExportColumn(`ext_${f.name}`)}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer transition-colors text-left"
                            >
                              <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                                selectedExportColumns[`ext_${f.name}`] ? 'bg-primary border-primary' : 'border-border bg-background'
                              }`}>{selectedExportColumns[`ext_${f.name}`] && <Check className="w-3 h-3 text-white" />}</div>
                              <span className="text-sm text-foreground">{f.name}</span>
                              <span className="text-xs text-muted-foreground ml-auto">{f.data_type}</span>
                            </button>
                          ))}
                        </>
                      )}
                      {formFields.length > 0 && (
                        <>
                          <div className="px-2 py-1 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Pre-Chat Form</div>
                          {formFields.map((f) => (
                            <button
                              key={`form_${f.name}`}
                              type="button"
                              onClick={() => toggleExportColumn(`form_${f.name}`)}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer transition-colors text-left"
                            >
                              <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                                selectedExportColumns[`form_${f.name}`] ? 'bg-primary border-primary' : 'border-border bg-background'
                              }`}>{selectedExportColumns[`form_${f.name}`] && <Check className="w-3 h-3 text-white" />}</div>
                              <span className="text-sm text-foreground">{f.label || f.name}</span>
                            </button>
                          ))}
                        </>
                      )}
                    </div>

                    <div className="p-3 border-t border-border">
                      <button
                        onClick={exportToCSV}
                        className="w-full px-4 py-2 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 flex items-center justify-center gap-2 text-sm font-medium"
                      >
                        <Download className="w-4 h-4" />
                        Export {Object.values(selectedExportColumns).filter(Boolean).length} Columns
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" text="Loading chat history..." />
            </div>
          ) : sessionsList.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No chat sessions found for the selected agent and date range.</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden shadow-card">
              <div className="overflow-x-auto max-w-full">
                <table className="w-full" style={{ minWidth: `${Math.max(600, (visibleBaseColumns.length + visibleExtractionFields.length + visibleFormFields.length) * 130)}px` }}>
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-border bg-muted/30 dark:bg-muted/50">
                      {visibleBaseColumns.map((col) => (
                        <th
                          key={col.key}
                          className={`${col.key === 'cost_usd' ? 'text-right' : 'text-left'} py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap`}
                        >
                          {col.label}
                        </th>
                      ))}
                      {visibleExtractionFields.map((field) => (
                        <th
                          key={field.name}
                          className="text-left py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap"
                          title={field.description}
                        >
                          <span className="flex items-center gap-1">
                            <Database className="w-3 h-3 text-primary/60" />
                            {field.name}
                          </span>
                        </th>
                      ))}
                      {visibleFormFields.map((field) => (
                        <th
                          key={`form_${field.name}`}
                          className="text-left py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap"
                          title={`Pre-chat form field: ${field.label || field.name}`}
                        >
                          <span className="flex items-center gap-1">
                            <ClipboardList className="w-3 h-3 text-warning/70" />
                            {field.label || field.name}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {sessionsList.map((session) => (
                      <tr key={session.id} className="border-b border-border/30 hover:bg-muted/20 dark:hover:bg-muted/30 transition-all duration-150 group">
                        {visibleBaseColumns.map((col) => (
                          <td key={col.key} className={`py-4 px-3 ${col.key === 'cost_usd' ? 'text-right' : ''}`}>
                            {renderCell(session, col.key)}
                          </td>
                        ))}
                        {visibleExtractionFields.map((field) => {
                          const value = session.extracted_data?.[field.name];
                          const display = value != null && value !== '' ? String(value) : '-';
                          return (
                            <td key={field.name} className="py-4 px-3">
                              <span
                                className={`text-sm ${display === '-' ? 'text-muted-foreground' : 'text-foreground'} font-medium truncate block max-w-[200px]`}
                                title={display}
                              >
                                {display}
                              </span>
                            </td>
                          );
                        })}
                        {visibleFormFields.map((field) => {
                          const value = session.form_variables?.[field.name];
                          const display = value != null && value !== '' ? String(value) : '-';
                          return (
                            <td key={`form_${field.name}`} className="py-4 px-3">
                              <span
                                className={`text-sm ${display === '-' ? 'text-muted-foreground' : 'text-foreground'} font-medium truncate block max-w-[200px]`}
                                title={display}
                              >
                                {display}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-muted-foreground order-2 sm:order-1">
                Showing {((currentPage - 1) * sessionsPerPage) + 1} to {Math.min(currentPage * sessionsPerPage, totalSessions)} of {totalSessions} chats
              </div>
              <div className="flex space-x-2 order-1 sm:order-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-card border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-foreground rounded-lg transition-colors"
                >
                  Previous
                </button>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 rounded-lg transition-colors ${
                        currentPage === pageNum ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:bg-muted text-foreground'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 bg-card border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-foreground rounded-lg transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {transcriptSession && (
        <TranscriptModal
          session={transcriptSession}
          onClose={() => setTranscriptSession(null)}
        />
      )}

    </div>
  );
};

export default ChatHistoryExport;
